import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { withLangPath, detectLangFromPath } from "@/i18n";
import { trackEvent } from "@/lib/analytics";

const STORAGE_KEY = "viralens_lang_suggest_dismissed";
const PREF_KEY = "viralens_lang";

interface SuggestLanguageBannerProps {
  autoRedirect?: boolean;
}

export function SuggestLanguageBanner({ autoRedirect = false }: SuggestLanguageBannerProps) {
  const [visible, setVisible] = useState(false);
  const [targetUrl, setTargetUrl] = useState<string>("");

  useEffect(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") return;

    if (detectLangFromPath() === "es") return;

    const browserLang = (navigator.language || "").toLowerCase();
    if (!browserLang.startsWith("es")) return;

    if (localStorage.getItem(STORAGE_KEY) === "1") return;

    const storedPref = localStorage.getItem(PREF_KEY);
    if (storedPref && storedPref !== "es") return;

    const search = new URLSearchParams(window.location.search);
    search.delete("lang");
    const searchStr = search.toString();
    const url =
      withLangPath(window.location.pathname, "es") +
      (searchStr ? `?${searchStr}` : "") +
      window.location.hash;

    if (autoRedirect && !storedPref) {
      localStorage.setItem(PREF_KEY, "es");
      trackEvent("lang_suggest_auto_redirect", { lang: "es", browserLang });
      window.location.replace(url);
      return;
    }

    setTargetUrl(url);
    setVisible(true);
    trackEvent("lang_suggest_banner_impression", { lang: "es", browserLang });
  }, [autoRedirect]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    trackEvent("lang_suggest_banner_dismissed", { lang: "es" });
    setVisible(false);
  };

  const accept = () => {
    localStorage.setItem(PREF_KEY, "es");
    localStorage.setItem(STORAGE_KEY, "1");
    trackEvent("lang_suggest_banner_accepted", { lang: "es" });
  };

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Sugerencia de idioma"
      lang="es"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-2rem)] max-w-md rounded-2xl border border-white/10 bg-[#141418]/95 backdrop-blur-xl shadow-2xl shadow-black/60 px-4 py-3 flex items-center gap-3"
    >
      <span className="text-2xl shrink-0" aria-hidden="true">🌐</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white font-medium leading-snug">
          ¿Prefieres español?
        </p>
        <a
          href={targetUrl}
          onClick={accept}
          className="inline-block mt-0.5 text-sm text-vl-orange hover:text-vl-amber font-semibold underline-offset-2 hover:underline"
        >
          Ver en español →
        </a>
      </div>
      <button
        onClick={dismiss}
        aria-label="Cerrar"
        className="shrink-0 p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
